import {Injectable} from '@angular/core';
import {ApiService} from '@core/services/api.service';
import {IResponseApi} from '@core/models';
import {uriAPIConsts, uriConsts} from '@shared/constants';
import {IPageLogin, IResJoinPage, IResLoginPage, IResMorePageMeta} from '@shared/models/login';
import {IMetaDatasetItem} from '@shared/models/report/report.model';

export interface ILoginNewPagesResponse {
  businesses: Array<{ id: number; name: string | null }>;
  items: Array<IPageLogin & {
    selected?: boolean;
    businessName?: string;
  }>;
}

export interface ILoginAddPagesResponse {
  success?: boolean;
}

export interface IRequestPermsParams {
  channel: number;
  origin: string;
  t: 'extend' | 'reauth' | 'marketing';
  businessId?: string;
  appleUser?: string;
}

export interface IRequestPermsResponse {
  redirectUrl: string;
}

export interface IThreadsCallbackParams {
  code: string;
  state: string;
}

export interface IThreadsCallbackResponse {
  pageIds: string[];
}

export interface IWhatsAppAuthCallbackParams {
  code: string;
  event?: string;
  waba_id?: string;
  phone_number_id?: string;
  current_step?: string;
  error_message?: string;
}

export interface IWhatsAppAuthCallbackResponse {
  pageIds: Array<string | number>;
}

export interface IFbReauthParams {
  code: string;
  redirectUri: string;
}

export type IFbReauthResponse = Record<string, any>;

export interface IFbReauthMetaCapiParams {
  pageIds: Array<string | number>;
  code: string;
}

export type IFbReauthMetaCapiResponse = IResponseApi<IMetaDatasetItem[]> | IMetaDatasetItem[];

@Injectable({
  providedIn: 'any'
})
export class LoginApiService extends ApiService {
  requestPerms(params: IRequestPermsParams) {
    return this.postUserRequest<IResponseApi<IRequestPermsResponse>>(
      uriConsts.auth.requestPerms, params
    );
  }

  /** Hoàn tất OAuth Threads sau khi BE callback chuyển code/state về FE. */
  threadsCallback(params: IThreadsCallbackParams) {
    return this.postUserRequest<IResponseApi<IThreadsCallbackResponse>>(
      uriConsts.auth.threadscallback, params
    );
  }

  whatsappCallback(params: IWhatsAppAuthCallbackParams) {
    return this.postUserRequest<IResponseApi<IWhatsAppAuthCallbackResponse>>(
      uriConsts.auth.whatsappcallback, params
    );
  }

  fbReauth(params: IFbReauthParams) {
    return this.postUserRequest<IResponseApi<IFbReauthResponse>>(
      uriConsts.auth.fbReauth, params
    );
  }

  fbReauthMetaCapi(params: IFbReauthMetaCapiParams) {
    return this.postUserRequest<IFbReauthMetaCapiResponse>(
      uriConsts.auth.fbReauthMetaCapi, params
    );
  }

  /**
   * chọn lại Page
   * */
  logOutPage() {
    return this.postUserRequest<IResponseApi>(
      uriAPIConsts.user.OUT_PAGE
    );
  }

  /**
   * lấy danh sách page kèm quyền
   * chưa đăng nhập chọn page của doanh nghiệp nào, nên ko có business bắn lên, cần post lên dạng ko có businessId
   */
  loadListPage() {
    return this.postUserRequest<IResponseApi<IResLoginPage>>(
      uriAPIConsts.login.LIST_PAGE
    );
  }

  /**
   * load thêm data quyền && page khi login
   */
  loadMorePageMeta(params: { after: string, isNeedSetSession: number }) {
    return this.postUserRequest<IResponseApi<IResMorePageMeta>>(
      uriAPIConsts.login.LOAD_MORE_PAGE_META, params
    );
  }

  userJoinPages(params: { ids: (string | number)[], affiliate: string }) {
    return this.postUserRequest<IResponseApi<IResJoinPage>>(
      uriAPIConsts.login.USER_JOIN_PAGES, params
    );
  }

  /**
   * Thông tin đăng nhập của phiên BE hiện tại (jwt, uid, pageIds...), chỉ đọc session.
   * data là chuỗi JSON → đọc bằng parseAuthData.
   */
  authMe() {
    return this.getUserRequest<IResponseApi>(uriConsts.auth.me);
  }

  /**
   * Kiểm tra quyền truy cập chạy ngầm: tài khoản/nhân viên bị khoá, giới hạn giờ, page bị gỡ/hết hạn.
   * data.action: 1 bình thường, 2 đăng xuất, 3 thoát page (xem AUTH_TRACKING_ACTION).
   */
  authTracking() {
    return this.postUserRequest<IResponseApi<{ action: number; message?: string }>>(uriConsts.auth.tracking);
  }

  /**
   * - Đăng ký nhận thông báo(app mobile)
   * @param params
   */
  subscribeNotification(params: { token: string; deviceName: string; pageIds: string[] }) {
    return this.postUserRequest<IResponseApi>(
      uriAPIConsts.notification.SUBSCRIBE_TO_TOPICS, params
    );
  }

  /**
   * - Hủy đăng ký nhận thông báo (app mobile iOS)
   * Port từ FE13: db.service.ts unSubscribeNotification()
   */
  unsubscribeNotification(params: { token: string }) {
    return this.postUserRequest<IResponseApi>(
      uriAPIConsts.notification.UNSUBSCRIBE_FROM_ALL_TOPICS, params
    );
  }

  subscribedAppFB(params: { pages: string }) {
    return this.postUserRequest<IResponseApi>(
      uriAPIConsts.chat.FACEBOOK.SUBSCRIBE_APP, params
    );
  }

  /**
   * Lấy thông tin token theo tokenId hoặc pageId WhatsApp sau join.
   */
  autoGetTokenInfo(params: { tokenId: string; pageId?: never } | { pageId: string; tokenId?: never }) {
    return this.postUserRequest<IResponseApi>(
      uriAPIConsts.chat.FACEBOOK.GET_TOKEN_INFO, params
    );
  }

  getNewPages(params: { pageIds: (string | number)[], channel?: number }) {
    return this.postUserRequest<IResponseApi<ILoginNewPagesResponse>>(
      uriAPIConsts.login.NEW_PAGES, params
    );
  }

  addPagesToBusiness(params: { businessId: string | number, pageIds: (string | number)[], channel: string | number }) {
    return this.postUserRequest<IResponseApi<ILoginAddPagesResponse>>(
      uriAPIConsts.login.ADD_PAGES, params
    );
  }

  /**
   * Kết nối lại Zalo Personal — port từ FE13 db.loginZalopersonal()
   * Gọi POST /auth/zalopersonal với act=connect để refresh token/QR scan lại.
   */
  reconnectZaloPersonal(params: { pageId: string }) {
    return this.postUserRequest<IResponseApi>(
      '/auth/zalopersonal', { act: 'connect', ...params }
    );
  }

  /**
   * Đăng xuất — huỷ session BE trước khi logout FE.
   */
  logout() {
    return this.postUserRequest<IResponseApi>('/auth/logout');
  }
}
